import { css } from "aphrodite";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@mui/material";
import { useNavigate } from "react-router";
import { useNavigationType, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import StepDataInfo from "../StepDataInfo/StepDataInfo";
import { MobileTitle } from "../MobileTitle";
import { RoutePath } from "../../../../Utils/routePath";
import {useSelector} from "react-redux";
import {Appointment} from "../../../../Models";
import {stepLayoutItemsId} from "../../../../Utils";

const StepInfo = ({
    activeStepId,
    layoutItems,
    customStatus,
    webView = true,
}) => {
    const [t] = useTranslation(["translation"]);
    const navigate = useNavigate();
    const location = useLocation();
    const navigationType = useNavigationType();
    const activeStep = layoutItems.find((item) => item.id === activeStepId);
    const selectedSubServiceGroupId = useSelector(Appointment.getSelectedSubServiceGroupId);
    const lastProcessedRef = useRef({ path: null, type: null, timestamp: 0 });

    const clearStepData = (stepId, clearDataArrays = false) => {
        switch (stepId) {
            case stepLayoutItemsId.selectBranch:
                Appointment.setSelectedBranch(null, null, null);
                if (clearDataArrays) {
                    Appointment.clearBranchesData();
                }
                break;
            case stepLayoutItemsId.selectService:
                Appointment.setSelectedServiceGroupId('');
                Appointment.setSelectedSubServiceGroupId('');
                Appointment.setSelectedService(null, null, null);
                if (clearDataArrays) {
                    Appointment.clearServicesData();
                }
                break;
            case stepLayoutItemsId.selectSubService:
                Appointment.setSelectedServiceGroupId('');
                Appointment.setSelectedService(null, null, null);
                break;
            case stepLayoutItemsId.selectDateAndTime:
                Appointment.setSelectedAvailableSlot(null);
                Appointment.visibilityForSelectedSlotData(false);
                break;
            case stepLayoutItemsId.verifyContactDetails:
                Appointment.setUserInformation(null);
                Appointment.setSelectedNumber(null);
                Appointment.setSelectedEmail(null);
                break;
            default:
                break;
        }
    };

    const clearFromStepOnwards = (targetStepId) => {
        const targetIndex = layoutItems.findIndex((item) => item.id === targetStepId);
        if (targetIndex < 0) return;
        for (let i = targetIndex; i < layoutItems.length; i++) {
            const shouldClearArrays = i > targetIndex;
            clearStepData(layoutItems[i].id, shouldClearArrays);
        }
    };

    
    useEffect(() => {
        if (navigationType === 'POP') {
            const currentPath = location.pathname;
            const now = Date.now();
            const isDifferentNavigation = 
                lastProcessedRef.current.path !== currentPath || 
                lastProcessedRef.current.type !== 'POP' ||
                now - lastProcessedRef.current.timestamp > 100; // 100ms debounce
            
            if (isDifferentNavigation) {
                lastProcessedRef.current = { path: currentPath, type: 'POP', timestamp: now };
                
                const currentIndex = layoutItems.findIndex((item) => item.id === activeStepId);
                if (currentIndex >= 0) {
                    for (let i = currentIndex; i < layoutItems.length; i++) {
                        const shouldClearArrays = i > currentIndex;
                        clearStepData(layoutItems[i].id, shouldClearArrays);
                    }
                }
            }
        } else if (navigationType === 'PUSH' || navigationType === 'REPLACE') {
            lastProcessedRef.current = { 
                path: location.pathname, 
                type: navigationType, 
                timestamp: Date.now() 
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, navigationType, webView]);

    const handleNavigation = (stepItem) => {
        const targetIndex = layoutItems.findIndex((item) => item.id === stepItem.id);
        if (targetIndex >= 0) {
            clearFromStepOnwards(stepItem.id);
        }
        navigate(stepItem.path, { replace: true });
    };

    const handleGoBack = () => {
        const activeStepIndex = layoutItems.findIndex(
            (item) => item.id === activeStep?.id
        );
        
        if (activeStepIndex > 0) {
            let prevStepIndex = activeStepIndex - 1;
            
            if (layoutItems[prevStepIndex]?.id === stepLayoutItemsId.selectSubService && 
                !selectedSubServiceGroupId) {
                prevStepIndex = prevStepIndex - 1;
            }
            
            if (prevStepIndex >= 0) {
                const prevStep = layoutItems[prevStepIndex];
                for (let i = prevStepIndex; i < layoutItems.length; i++) {
                    const shouldClearArrays = i > prevStepIndex;
                    clearStepData(layoutItems[i].id, shouldClearArrays);
                }
                
                navigate(prevStep.path, { replace: true });
            } else {
                navigate(RoutePath.appointmentPage);
            }
        } else {
            navigate(RoutePath.appointmentPage);
        }
    };

    const handleGoHome = () => {
        Appointment.resetState();
        navigate(RoutePath.appointmentPage);
    };

    return webView ? (
        <div className={css(styles.container)}>
            {/* Steps or Status */}
            {customStatus ? (
                <Typography variant="h5">{t(customStatus)}</Typography>
            ) : (
                <div className={css(styles.stepsContainer)}>
                    {activeStep?.editMode ? (
                        <Button
                            variant="text"
                            color={"primary"}
                            onClick={handleGoHome}
                            disableElevation
                            disableRipple
                            style={{
                                padding: 0,
                                textTransform: "none",
                                minWidth: "auto",
                                whiteSpace: "nowrap",
                            }}
                        >
                            <Typography variant="h5" color="primary">
                                {t("Edit Appointment")}
                            </Typography>
                        </Button>
                    ) : (
                        <Button
                            variant="text"
                            color={"primary"}
                            onClick={handleGoHome}
                            disableElevation
                            disableRipple
                            style={{
                                padding: 0,
                                textTransform: "none",
                                minWidth: "auto",
                                whiteSpace: "nowrap",
                            }}
                        >
                            <Typography variant="h5" color="primary">
                                {t("Home Page")}
                            </Typography>
                        </Button>
                    )}
                    {layoutItems
                        .slice(
                            0,
                            layoutItems.findIndex(
                                (item) => item.id === activeStep?.id
                            ) + 1
                        )
                        .map((item, index) => {
                            if (!selectedSubServiceGroupId && item.id === stepLayoutItemsId.selectSubService) {
                                return null;
                            }
                            return (
                                <div key={item.id} className={css(styles.stepsRow)}>
                                    <Typography variant="h5">
                                        &nbsp;/&nbsp;
                                    </Typography>
                                    <Button
                                        variant="text"
                                        color={
                                            item.id !== activeStep.id
                                                ? "primary"
                                                : "inherit"
                                        }
                                        onClick={() => {
                                            handleNavigation(item);
                                        }}
                                        disableElevation
                                        disableRipple
                                        style={{
                                            padding: 0,
                                            textTransform: "none",
                                            minWidth: "auto",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        <Typography variant="h5">
                                            {t(item.value)}
                                        </Typography>
                                    </Button>
                                </div>
                            )
                        })}
                </div>
            )}
            <StepDataInfo />
            {/* Title of Page */}
            {activeStep && (
                <div className={css(styles.titleRow)}>
                    <img alt="" src={activeStep.icon} />
                    <Typography variant="h4">{t(activeStep.value)}</Typography>
                </div>
            )}
        </div>
    ) : (
        <MobileTitle
            iconSrc={activeStep.icon}
            title={t(activeStep.mobileValue)}
            onClickBackButton={handleGoBack}
        />
    );
};

export default StepInfo;
